import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Save as SaveIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';

// Event types
const eventTypes = [
  'Workshop',
  'Conference',
  'Concert',
  'Festival',
  'Networking',
  'Social',
  'Party',
  'Class',
  'Sport',
  'Competition',
  'Other'
];

// Form validation schema
const validationSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Description is required'),
  eventType: Yup.string().required('Event type is required'),
  location: Yup.string().required('Location is required'),
  startDate: Yup.date().required('Start date is required'),
  endDate: Yup.date()
    .required('End date is required')
    .min(Yup.ref('startDate'), 'End date must be after start date'),
  maxCapacity: Yup.number()
    .required('Maximum capacity is required')
    .min(1, 'Capacity must be at least 1')
    .integer('Capacity must be a whole number')
});

// Form values interface
interface EventFormValues {
  title: string;
  description: string;
  eventType: string;
  isPublic: boolean;
  location: string;
  startDate: Date;
  endDate: Date;
  isPaid: boolean;
  price: number;
  maxCapacity: number;
  coverPhoto: File | null;
}

/**
 * EditOrgEvent - Component for editing organization events
 */
const EditOrgEvent: React.FC = () => {
  const navigate = useNavigate();
  const { id, eventId } = useParams<{ id: string; eventId: string }>();
  const { activeOrganization } = useOrganization();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | null>(null);
  
  // Initialize formik
  const formik = useFormik<EventFormValues>({
    initialValues: {
      title: '',
      description: '',
      eventType: '',
      isPublic: true,
      location: '',
      startDate: new Date(),
      endDate: new Date(new Date().getTime() + 2 * 60 * 60 * 1000), // 2 hours later
      isPaid: false,
      price: 0,
      maxCapacity: 100,
      coverPhoto: null
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!id || !eventId) {
        setError('Missing organization or event ID');
        return;
      }
      
      try {
         setSaving(true);

         // Prepare data for Firestore
         const eventData: any = {
            title: values.title,
            description: values.description,
            eventType: values.eventType,
            isPublic: values.isPublic,
            location: values.location,
            startDate: values.startDate,
            endDate: values.endDate,
            isPaid: values.isPaid,
            price: values.isPaid ? values.price : 0,
            maxCapacity: values.maxCapacity,
            lastUpdated: new Date(),
         };

         // Upload new cover photo if provided
         if (values.coverPhoto) {
            const storage = getStorage();
            const storageRef = ref(storage, `organizations/${id}/events/${eventId}/cover`);

            const uploadTask = await uploadBytesResumable(storageRef, values.coverPhoto);
            const downloadUrl = await getDownloadURL(uploadTask.ref);

            eventData.coverPhotoUrl = downloadUrl;
         } else if (coverPhotoUrl) {
            // Keep existing photo if no new one uploaded
            eventData.coverPhotoUrl = coverPhotoUrl;
         }

         // Update event in Firestore
         const eventRef = doc(db, 'events', eventId);
         await updateDoc(eventRef, eventData);

         setSaving(false);

         // Navigate back to event detail
         navigate(`/organizations/${id}/events/${eventId}`);
      } catch (err) {
        console.error('Error saving event:', err);
        setError('Failed to save event. Please try again.');
        setSaving(false);
      }
    },
  });
  
  useEffect(() => {
    fetchEventDetails();
  }, [id, eventId]);
  
  // Function to fetch event details
  const fetchEventDetails = async () => {
    if (!id || !eventId) {
      setError('Missing organization or event ID');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);

      
      const eventRef = doc(db, 'events', eventId);
      const eventSnapshot = await getDoc(eventRef);

      if (!eventSnapshot.exists()) {
         setError('Event not found');
         setLoading(false);
         return;
      }

      const eventData = eventSnapshot.data();

      
      // Handle date conversion
      let startDate = new Date();
      let endDate = new Date();
      
      if (eventData.startDate) {
        if (eventData.startDate instanceof Timestamp) {
          startDate = eventData.startDate.toDate();
        } else if (eventData.startDate.seconds) {
          startDate = new Date(eventData.startDate.seconds * 1000);
        } else if (eventData.startDate.toDate) {
          startDate = eventData.startDate.toDate();
        } else {
          startDate = new Date(eventData.startDate);
        }
      }
      
      if (eventData.endDate) {
        if (eventData.endDate instanceof Timestamp) {
          endDate = eventData.endDate.toDate();
        } else if (eventData.endDate.seconds) {
          endDate = new Date(eventData.endDate.seconds * 1000);
        } else if (eventData.endDate.toDate) {
          endDate = eventData.endDate.toDate();
        } else {
          endDate = new Date(eventData.endDate);
        }
      }
      
      // Extract location string from potentially complex location object
      let locationStr = '';
      if (typeof eventData.location === 'string') {
        locationStr = eventData.location;
      } else if (eventData.location && typeof eventData.location === 'object') {
        locationStr = eventData.location.name || eventData.location.address || '';
      }
      
      // Set form values from event data
      formik.setValues({
        title: eventData.title || '',
        description: eventData.description || '',
        eventType: eventData.eventType || '',
        isPublic: eventData.isPublic !== false, // default to true if not specified
        location: locationStr,
        startDate,
        endDate,
        isPaid: eventData.isPaid || false,
        price: eventData.price || 0,
        maxCapacity: eventData.maxCapacity || 100,
        coverPhoto: null // We don't load the file, just the URL
      });
      
      // Store cover photo URL separately
      if (eventData.coverPhotoUrl) {
        setCoverPhotoUrl(eventData.coverPhotoUrl);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading event details:', err);
      setError('Failed to load event details. Please try again.');
      setLoading(false);
    }
  };
  
  // Handle file selection for cover photo
  const handleCoverPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      formik.setFieldValue('coverPhoto', file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          setCoverPhotoPreview(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Navigation back to event detail
  const handleGoBack = () => {
    if (id && eventId) {
      navigate(`/organizations/${id}/events/${eventId}`);
    } else if (id) {
      navigate(`/organizations/${id}/events`);
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading event details...
          </Typography>
        </Box>
      </Container>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
        >
          Back to Event
        </Button>
      </Container>
    );
  }
  
  // Main form render
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{ mr: 2 }}
            >
              Back to Event
            </Button>
            
            <Typography variant="h5" component="h1">
              Edit Event for {activeOrganization?.name || 'Organization'}
            </Typography>
          </Box>
          
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* Event Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="title"
                  name="title"
                  label="Event Title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {/* Event Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  name="description"
                  label="Event Description"
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {/* Event Type & Visibility */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={formik.touched.eventType && Boolean(formik.errors.eventType)}>
                  <InputLabel id="event-type-label">Event Type</InputLabel>
                  <Select
                    labelId="event-type-label"
                    id="eventType"
                    name="eventType"
                    value={formik.values.eventType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Event Type"
                  >
                    {eventTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                  {formik.touched.eventType && formik.errors.eventType && (
                    <FormHelperText>{formik.errors.eventType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      id="isPublic"
                      name="isPublic"
                      checked={formik.values.isPublic}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Public Event (visible to all users)"
                />
              </Grid>
              
              {/* Location */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="location"
                  name="location"
                  label="Event Location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.location && Boolean(formik.errors.location)}
                  helperText={formik.touched.location && formik.errors.location}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {/* Start & End Date */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Date and Time
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Start Date and Time"
                  value={formik.values.startDate}
                  onChange={(newValue) => {
                    formik.setFieldValue('startDate', newValue);
                  }}
                 
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth required error={formik.touched.startDate && Boolean(formik.errors.startDate)} helperText={formik.touched.startDate && formik.errors.startDate ? String(formik.errors.startDate) : null} />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="End Date and Time"
                  value={formik.values.endDate}
                  onChange={(newValue) => {
                    formik.setFieldValue('endDate', newValue);
                  }}
                 
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth required error={formik.touched.endDate && Boolean(formik.errors.endDate)} helperText={formik.touched.endDate && formik.errors.endDate ? String(formik.errors.endDate) : null} />
                  )}
                />
              </Grid>
              
              {/* Divider */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              {/* Price & Capacity */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Ticketing
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      id="isPaid"
                      name="isPaid"
                      checked={formik.values.isPaid}
                      onChange={formik.handleChange}
                    />
                  }
                  label="This is a paid event"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="price"
                  name="price"
                  label="Ticket Price"
                  type="number"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  disabled={!formik.values.isPaid}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="maxCapacity"
                  name="maxCapacity"
                  label="Maximum Capacity"
                  type="number"
                  value={formik.values.maxCapacity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.maxCapacity && Boolean(formik.errors.maxCapacity)}
                  helperText={
                    (formik.touched.maxCapacity && formik.errors.maxCapacity) ||
                    "Maximum number of attendees allowed"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PeopleIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {/* Divider */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              {/* Cover Photo */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Cover Photo
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="cover-photo-upload"
                    type="file"
                    onChange={handleCoverPhotoChange}
                  />
                  <label htmlFor="cover-photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<ImageIcon />}
                    >
                      {coverPhotoUrl ? 'Change Cover Photo' : 'Upload Cover Photo'}
                    </Button>
                  </label>
                </Box>
                
                {(coverPhotoPreview || coverPhotoUrl) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {coverPhotoPreview ? 'New Cover Photo Preview:' : 'Current Cover Photo:'}
                    </Typography>
                    <Box
                      component="img"
                      src={coverPhotoPreview || coverPhotoUrl || ''}
                      alt="Cover Photo"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  </Box>
                )}
              </Grid>
              
              {/* Submit Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleGoBack}
                    sx={{ mr: 2 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default EditOrgEvent;
